import { useContext, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useQueryClient } from "@tanstack/react-query";
import * as yup from "yup";

import MyButton from "../../../components/MyButton";
import MyTextField from "../../../components/inputs/MyTextField";
import MyInputFile from "../../../components/inputs/MyInputFile";
import { doUploadFiles, doUploadSingleFile } from "../../../utils/file";
import { useFile } from "../../../hooks/useFile";
import { useBoardItem } from "../../../hooks/useBoardItem";
import { SiteContext } from "../../../context/SiteProvider";
import { AuthContext } from "../../../context/AuthProvider";
import MyDatePicker from "../../../components/inputs/MyDatePicker";
import {
  BOARD_PERMISSION,
  NON_MEMBER_PERMISSIONS,
} from "../../../constants/permission";
import {
  CheckRoleAndPermissionDisplay,
  checkRoleAndPermissions,
  getPermissionMessage,
} from "../../../components/CheckRoleAndPermission";

const NewsUpsert02 = ({ boardCode }) => {
  const navigate = useNavigate();
  const { itemNo } = useParams();
  const { getFileUploadUrl } = useFile();
  const {
    createBoardItemMutation,
    updateBoardItemMutation,
    useGetBoardItem,
    useGetBoard,
  } = useBoardItem();
  const queryClient = useQueryClient();
  const { setLoadingScreen } = useContext(SiteContext);
  const { profile, roleCode, isAdmin, isAuthenticated } =
    useContext(AuthContext);

  const nonPermissions = NON_MEMBER_PERMISSIONS[boardCode];

  const { data: board, isLoading: isBoardLoading } = useGetBoard(
    { boardCode },
    (data) => data.data
  );

  const { data, isLoading } = useGetBoardItem(
    {
      boardCode: board?.boardCode,
      itemNo,
      enabled:
        checkRoleAndPermissions(
          roleCode,
          board,
          BOARD_PERMISSION.READ,
          nonPermissions,
          isAdmin
        ) &&
        checkRoleAndPermissions(
          roleCode,
          board,
          BOARD_PERMISSION.WRITE,
          nonPermissions,
          isAdmin
        ),
    },
    (data) => data.data
  );

  const initValues = {
    title: "",
    publisher: "",
    publishedDate: null,
    thumbnail: null,
    hasFile: false,
    fileKr: null,
    fileEn: null,
    embeddedFiles: [],
  };

  const validationSchema = yup.object({
    title: yup.string().required("필수 입력 항목입니다."),
    publisher: yup.string().required("필수 입력 항목입니다."),
    publishedDate: yup
      .date()
      .typeError("올바른 날짜를 입력하세요.")
      .required("필수 입력 항목입니다."),
    thumbnail: yup.mixed().required("파일을 선택해 주세요."),
    hasFile: yup
      .boolean()
      .oneOf(
        [true],
        "국문 또는 영문 파일 중 하나를 첨부해 주세요. (모두 첨부 가능)"
      ),
    fileEn: yup
      .mixed()
      .nullable()
      .when("hasFile", {
        is: (hasFile) => !hasFile,
        then: () =>
          yup
            .mixed()
            .required(
              "국문 또는 영문 파일 중 하나를 첨부해 주세요. (모두 첨부 가능)"
            ),
      }),
    fileKr: yup
      .mixed()
      .nullable()
      .when("hasFile", {
        is: (hasFile) => !hasFile,
        then: () =>
          yup
            .mixed()
            .required(
              "국문 또는 영문 파일 중 하나를 첨부해 주세요. (모두 첨부 가능)"
            ),
      }),
  });

  const methods = useForm({
    defaultValues: initValues,
    resolver: yupResolver(validationSchema),
  });

  const onFileChange = () => {
    const fileKr = methods.getValues("fileKr");
    const fileEn = methods.getValues("fileEn");
    methods.clearErrors("hasFile");
    methods.setValue("hasFile", !!fileKr || !!fileEn);
  };

  const onSubmit = async (vals) => {
    if (
      checkRoleAndPermissions(
        roleCode,
        board,
        BOARD_PERMISSION.WRITE,
        nonPermissions,
        isAdmin
      )
    ) {
      setLoadingScreen(true);
      let uploadedFiles = [];
      const files = [];
      if (vals.fileKr) files.push(vals.fileKr);
      if (vals.fileEn) files.push(vals.fileEn);

      if (files.length) {
        uploadedFiles = await doUploadFiles(files, getFileUploadUrl, {
          boardCode: board?.boardCode,
        });
      }

      let uploadedThumbnailFile = {};
      if (vals.thumbnail) {
        uploadedThumbnailFile = await doUploadSingleFile(
          vals.thumbnail,
          getFileUploadUrl,
          {
            boardCode: board?.boardCode,
          }
        );
      }

      const payload = {
        title: vals.title,
        body: vals.body,
        userId: data?.writer?.userId ? data.writer.userId : profile.userId,
        additionalItems: {
          publisher: vals.publisher,
          publishedDate: vals.publishedDate,
        },
        attachedFiles: uploadedFiles.map((file) => ({
          fileName: file.name,
          fileId: file.fileId,
        })),
        thumbnailFiles: [
          {
            fileName: uploadedThumbnailFile.name,
            fileId: uploadedThumbnailFile.fileId,
          },
        ],
      };

      if (!itemNo) {
        createBoardItemMutation.mutate(
          { boardCode: board?.boardCode, payload },
          {
            onSuccess: (res) => {
              if (res) {
                methods.reset();
                alert("게시글 작성이 완료되었습니다.");
                queryClient.invalidateQueries({
                  queryKey: ["/board-item?boardCode={boardCode}"],
                });
                navigate("/News/AnnualReport");
              } else {
                alert("게시물 작성 또는 수정 중 오류가 발생했습니다.");
              }
            },
            onError: () => {
              alert("게시물 작성 또는 수정 중 오류가 발생했습니다.");
            },
            onSettled: () => {
              setLoadingScreen(false);
            },
          }
        );
      } else {
        updateBoardItemMutation.mutate(
          { boardCode: board?.boardCode, itemNo, payload },
          {
            onSuccess: (res) => {
              if (res) {
                alert("게시물이 성공적으로 업데이트되었습니다.");
                queryClient.invalidateQueries({
                  queryKey: ["/board-item?boardCode={boardCode}"],
                });
                navigate(`/News/AnnualReport`);
              } else {
                alert("게시물 작성 또는 수정 중 오류가 발생했습니다.");
              }
            },
            onError: () => {
              alert("게시물 작성 또는 수정 중 오류가 발생했습니다.");
            },
            onSettled: () => {
              setLoadingScreen(false);
            },
          }
        );
      }
    } else {
      alert(getPermissionMessage(BOARD_PERMISSION.WRITE));
    }
  };

  useEffect(() => {
    if (!isLoading && data) {
      methods.setValue("title", data.title);
      methods.setValue("publisher", data.additionalItems.publisher);
      methods.setValue("publishedDate", data.additionalItems.publishedDate);

      if (data.attachedFiles?.length) {
        methods.setValue("hasFile", true);

        const newFiles = data.attachedFiles.map((file) => ({
          fileId: file.fileId,
          name: file.fileName,
          file,
        }));

        const fileKr = newFiles.find((c) => c.name.startsWith("[KR]"));
        if (fileKr) methods.setValue("fileKr", fileKr);

        const fileEn = newFiles.find((c) => c.name.startsWith("[EN]"));
        if (fileEn) methods.setValue("fileEn", fileEn);
      }

      if (data.thumbnailFiles?.length) {
        methods.setValue("thumbnail", {
          fileId: data.thumbnailFiles[0].fileId,
          name: data.thumbnailFiles[0].fileName,
          file: data.thumbnailFiles[0],
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, data]);

  useEffect(() => {
    if (
      !isBoardLoading &&
      !!board &&
      !checkRoleAndPermissions(
        roleCode,
        board,
        BOARD_PERMISSION.WRITE,
        nonPermissions,
        isAdmin
      )
    )
      alert(getPermissionMessage(BOARD_PERMISSION.WRITE));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board, isBoardLoading, roleCode, isAdmin]);

  return (
    <CheckRoleAndPermissionDisplay
      showMessage={!isBoardLoading && !!board}
      board={board}
      permission={BOARD_PERMISSION.WRITE}
      boardCode={boardCode}
    >
      <div className="border-t-2 border-primary md:pt-10 pt-6">
        <h2 className="font-semibold lg:text-3xl md:text-2xl text-xl mb-4">
          글 {!itemNo ? "작성" : "수정"}
        </h2>

        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <div className="flex flex-wrap md:mb-5 mb-3">
              <label
                htmlFor="title"
                className="md:mt-4 md:w-[150px] w-full font-semibold"
              >
                제목 <span className="text-error">*</span>
              </label>
              <div className="flex-1">
                <MyTextField
                  id="title"
                  name="title"
                  placeholder="제목을 입력해주세요."
                />
              </div>
            </div>

            <div className="flex flex-wrap md:mb-5 mb-3">
              <label
                htmlFor="publisher"
                className="md:mt-4 md:w-[150px] w-full font-semibold"
              >
                발행인 <span className="text-error">*</span>
              </label>
              <div className="flex-1">
                <MyTextField
                  id="publisher"
                  name="publisher"
                  placeholder="발행인 항목을 입력해주세요."
                />
              </div>
            </div>

            <div className="flex flex-wrap md:mb-5 mb-3">
              <label
                htmlFor="publishedDate"
                className="md:mt-4 md:w-[150px] w-full font-semibold"
              >
                발행일 <span className="text-error">*</span>
              </label>
              <div className="flex-1">
                <MyDatePicker
                  id="publishedDate"
                  name="publishedDate"
                  placeholder="발행일을 입력해주세요."
                />
              </div>
            </div>

            <div className="flex flex-wrap md:mb-5 mb-3">
              <label
                htmlFor="thumbnail"
                className="md:mt-4 md:w-[150px] w-full font-semibold"
              >
                썸네일 첨부 <span className="text-error">*</span>
              </label>
              <div className="flex-1">
                <MyInputFile
                  name="thumbnail"
                  placeholder="파일을 첨부해 주세요."
                  hint="*이미지 파일을 첨부해주세요"
                  accept="image/*"
                />
              </div>
            </div>

            <div className="flex flex-wrap md:mb-5 mb-3">
              <label
                htmlFor="fileKr"
                className="md:mt-4 md:w-[150px] w-full font-semibold"
              >
                국문 파일 첨부
              </label>
              <div className="flex-1">
                <MyInputFile
                  name="fileKr"
                  placeholder="파일을 첨부해 주세요."
                  hint="*PDF 파일을 첨부해주세요."
                  accept="application/pdf"
                  prefix="[KR]"
                  onChange={onFileChange}
                />
              </div>
            </div>

            <div className="flex flex-wrap md:mb-5 mb-3">
              <label
                htmlFor="fileEn"
                className="md:mt-4 md:w-[150px] w-full font-semibold"
              >
                영문 파일 첨부
              </label>
              <div className="flex-1">
                <MyInputFile
                  name="fileEn"
                  placeholder="파일을 첨부해 주세요."
                  hint="*PDF 파일을 첨부해주세요."
                  accept="application/pdf"
                  prefix="[EN]"
                  onChange={onFileChange}
                />
              </div>
            </div>

            <div className="flex items-center md:mt-10 mt-6 md:gap-4 gap-2">
              <MyButton
                variant="contained"
                color="#111111"
                className="font-semibold sm:min-w-[120px] mr-auto text-white"
                onClick={() =>
                  checkRoleAndPermissions(
                    roleCode,
                    board,
                    BOARD_PERMISSION.ACCESS,
                    nonPermissions,
                    isAdmin
                  )
                    ? navigate("/News/AnnualReport")
                    : alert(getPermissionMessage(BOARD_PERMISSION.ACCESS))
                }
              >
                목록보기
              </MyButton>

              <MyButton
                variant="outlined"
                className="sm:min-w-[120px]"
                onClick={() => {
                  if (itemNo) {
                    checkRoleAndPermissions(
                      roleCode,
                      board,
                      BOARD_PERMISSION.READ,
                      nonPermissions,
                      isAdmin
                    )
                      ? navigate(`/News/AnnualReport/${itemNo}`)
                      : alert(getPermissionMessage(BOARD_PERMISSION.READ));
                  } else {
                    checkRoleAndPermissions(
                      roleCode,
                      board,
                      BOARD_PERMISSION.ACCESS,
                      nonPermissions,
                      isAdmin
                    )
                      ? navigate("/News/AnnualReport")
                      : alert(getPermissionMessage(BOARD_PERMISSION.ACCESS));
                  }
                }}
              >
                <span className="font-semibold">취소</span>
              </MyButton>

              {!itemNo ||
              (!!itemNo &&
                isAuthenticated &&
                (profile?.userId === data?.writer?.userId || isAdmin)) ? (
                <MyButton
                  variant="contained"
                  color="primary"
                  className="font-semibold sm:min-w-[120px]"
                  type="submit"
                >
                  저장
                </MyButton>
              ) : null}
            </div>
          </form>
        </FormProvider>
      </div>
    </CheckRoleAndPermissionDisplay>
  );
};

export default NewsUpsert02;
